<template>
  <div class="mention-input-container">
    <textarea
        ref="inputEl"
        :value="displayText"
        @input="handleInput"
        @keydown="handleKeyDown"
        placeholder="输入 @ 提示关键词"
    ></textarea>

    <div v-if="showDropdown" class="mention-dropdown">
      <div
          v-for="(item, index) in filteredOptions"
          :key="item.id"
          :class="['dropdown-item', { active: index === activeIndex }]"
          @click="selectItem(item)"
      >
        {{ item.name }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MentionInput',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedItem: {
      type: String,
      default: ''
    },
    textareaStr: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      inputText: '',
      showDropdown: false,
      activeIndex: 0,
      mentionStartPos: null,
      searchQuery: '',
      mentionMap: new Map()
    }
  },
  computed: {
    filteredOptions() {
      return this.options.filter(option =>
          option.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    selectBeans() {
      return Array.from(this.mentionMap.values()).join('')
    },
    convertedText() {
      let result = this.inputText;
      for (const [displayText, backendValue] of this.mentionMap) {
        result = result.replace(new RegExp(displayText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), backendValue);
      }
      return result;
    },
    displayText() {
      let result = this.inputText;
      for (const [displayText, backendValue] of this.mentionMap) {
        result = result.replace(new RegExp(backendValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), displayText);
      }
      return result;
    }
  },
  watch: {
    selectedItem: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.parseSelectedItem(newVal);
        } else {
          this.mentionMap.clear();
        }
      }
    },
    textareaStr: {
      immediate: true,
      handler(newVal) {
        this.inputText = newVal || '';
        this.syncMentionMap();
      }
    }
  },
  methods: {
    clearContent() {
      this.inputText = '';
      this.mentionMap.clear();
      this.$emit('update:textareaStr', '');
      this.$emit('mention-selected', '');
    },

    parseSelectedItem(selectedItem) {
      this.mentionMap.clear();
      this.inputText = selectedItem;
      const regex = /@\{([^}]+)\}/g;
      let match;

      while ((match = regex.exec(selectedItem))){
        const value = `@{${match[1]}}`;
        const option = this.options.find(opt => opt.value === value);
        if (option) {
          this.mentionMap.set(`@${option.name}`, value);
        }
      }
    },

    handleInput(event) {
      const displayValue = event.target.value;
      // 将显示文本转换为内部存储格式
      let internalText = displayValue;
      for (const [displayText, backendValue] of this.mentionMap) {
        internalText = internalText.replace(new RegExp(displayText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), backendValue);
      }
      this.inputText = internalText;

      const cursorPos = event.target.selectionStart;
      const textBeforeCursor = displayValue.substring(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');

      if (atIndex !== -1 && !textBeforeCursor.substring(atIndex).includes(' ')) {
        this.mentionStartPos = atIndex;
        this.searchQuery = textBeforeCursor.substring(atIndex + 1);
        this.showDropdown = true;
        this.activeIndex = 0;
      } else {
        this.showDropdown = false;
        this.syncMentionMap();
      }
      this.$emit('update:textareaStr', this.convertedText);
    },

    syncMentionMap() {
      if (this.showDropdown) return;

      const newMentionMap = new Map();
      const regex = /@([^\s@]+)/g;
      let match;

      while ((match = regex.exec(this.inputText))) {
        const mentionText = match[0];
        const option = this.options.find(opt => `@${opt.name}` === mentionText);
        if (option) {
          const backendValue = option.value.startsWith('@') ? option.value : `@${option.value}`;
          newMentionMap.set(mentionText, backendValue);
        }
      }

      this.mentionMap = newMentionMap;
      this.$emit('mention-selected', this.selectBeans);
      this.$emit('mention-inputText', this.convertedText);
    },

    handleKeyDown(event) {
      if (!this.showDropdown) return;

      switch(event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.activeIndex = Math.max(0, this.activeIndex - 1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.activeIndex = Math.min(this.filteredOptions.length - 1, this.activeIndex + 1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.filteredOptions[this.activeIndex]) {
            this.selectItem(this.filteredOptions[this.activeIndex]);
          }
          break;
        case 'Escape':
          this.showDropdown = false;
          break;
      }
    },

    selectItem(item) {
      if (this.mentionStartPos === null) return;

      const currentDisplayText = this.displayText;
      const textBefore = currentDisplayText.substring(0, this.mentionStartPos);
      const currentCursorPos = this.$refs.inputEl.selectionStart;
      // const textAfter = currentDisplayText.substring(currentCursorPos);

      const displayText = `@${item.name}`;
      const backendValue = item.value.startsWith('@') ? item.value : `@${item.value}`;

      // 更新内部存储文本
      const internalTextBefore = this.inputText.substring(0, this.mentionStartPos);
      const internalTextAfter = this.inputText.substring(currentCursorPos);
      this.inputText = `${internalTextBefore}${backendValue} ${internalTextAfter}`;

      this.mentionMap.set(displayText, backendValue);
      this.showDropdown = false;

      this.$nextTick(() => {
        this.$refs.inputEl.focus();
        const newCursorPos = (textBefore + displayText + ' ').length;
        this.$refs.inputEl.setSelectionRange(newCursorPos, newCursorPos);
        this.$emit('update:textareaStr', this.convertedText);
        this.$emit('mention-selected', this.selectBeans);
        this.$emit('mention-inputText', this.convertedText);
      });
    }
  }
}
</script>

<style scoped>
.mention-input-container {
  position: relative;
  width: 100%;
}

textarea {
  width: 100%;
  min-height: 100px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
}

.mention-dropdown {
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 200px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
}

.dropdown-item:hover, .dropdown-item.active {
  background-color: #f0f0f0;
}
</style>
